import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ImageLightboxComponent } from '../../../shared/components/ui/image-lightbox/image-lightbox.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { KycService } from '../../../shared/services/kyc.service';
import { OperatorsService } from '../../../shared/services/operators.service';
import { CommissionService } from '../../../shared/services/zones.service';
import {
  KYC_DOCUMENT_TYPE_LABEL,
  KYC_STATUS_META,
  KycDocument,
  OPERATOR_KYC_STATUS_META,
} from '../../../shared/types/kyc.types';
import {
  EffectiveRatesResponse,
  LICENSE_TIER_LABEL,
  LICENSE_TIER_OPTIONS,
  LicenseTier,
  OPERATOR_STATUS_META,
  Operator,
  OperatorKycStatus,
} from '../../../shared/types/operator.types';
import { CommissionConfig } from '../../../shared/types/zone.types';

/** Extensions d'image reconnues → aperçu lightbox (sinon : lien « Ouvrir »). */
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|avif)(\?|$)/i;

const TIER_OPTIONS: PrettySelectOption<LicenseTier>[] = LICENSE_TIER_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}));

/**
 * Fiche opérateur admin. Porte **tout le traitement KYC** (#66/#93) : la section
 * « Justificatifs KYC » liste **tous** les documents de l'opérateur, tous statuts
 * (`GET /api/kyc/subject`), avec validation **par pièce**, **et** la décision sur
 * le **dossier** (`/admin/operators/{id}/kyc/approve|reject`). Les deux niveaux
 * sont indépendants côté back et n'ont pas le même effet :
 * - **pièce** validée → arme le cron d'expiration (rappels J-30/J-15/J-7) ;
 * - **dossier** validé → débloque le recrutement de prestataires + notifie.
 *
 * `/operator-kyc` n'est qu'une **boîte de réception** (« qui attend ») : on
 * inspecte et on statue ici, sans aller-retour.
 */
@Component({
  selector: 'app-operator-detail',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    LocalizedDatePipe,
    DecimalPipe,
    PageHeaderComponent,
    DataStateComponent,
    ModalComponent,
    PrettySelectComponent,
    UserCellComponent,
    ImageLightboxComponent,
  ],
  templateUrl: './operator-detail.component.html',
})
export class OperatorDetailComponent implements OnInit {
  private readonly service = inject(OperatorsService);
  private readonly commissionService = inject(CommissionService);
  private readonly kyc = inject(KycService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly op = signal<Operator | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  /** Effective rates merged from override + global. Lazy-loaded with `fetch()`. */
  readonly effectiveRates = signal<EffectiveRatesResponse | null>(null);
  /** Global config — shown as suggested values + as readout when no override. */
  readonly globalConfig = signal<CommissionConfig | null>(null);

  readonly statusMeta = OPERATOR_STATUS_META;
  readonly tierLabel = LICENSE_TIER_LABEL;
  readonly tierOptions = TIER_OPTIONS;

  // ── Justificatifs KYC (#66) ───────────────────────────────────────────
  // Tous les documents de l'opérateur, quel que soit leur statut — contrairement
  // à la file `/kyc` qui ne liste que le backlog PENDING, tous acteurs confondus.
  readonly dossierMeta = OPERATOR_KYC_STATUS_META;
  readonly docStatusMeta = KYC_STATUS_META;
  readonly docTypeLabel = KYC_DOCUMENT_TYPE_LABEL;
  readonly kycDocs = signal<KycDocument[]>([]);
  readonly kycLoading = signal(false);
  readonly kycError = signal<string | null>(null);
  /** Document en cours de validation (spinner sur sa ligne). */
  readonly kycActingId = signal<number | null>(null);
  readonly lightboxSrc = signal<string | null>(null);

  // ── Rejet d'un document KYC ───────────────────────────────────────────
  readonly docRejectOpen = signal(false);
  readonly docRejectTarget = signal<KycDocument | null>(null);
  readonly docRejecting = signal(false);
  readonly docRejectError = signal<string | null>(null);
  readonly docRejectForm = this.fb.group({
    reason: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(500)]],
  });

  // ── Décision sur le dossier KYC (#66) ─────────────────────────────────
  // Statuer ici évite l'aller-retour vers /operator-kyc : les pièces sont
  // sous les yeux au moment de trancher.
  readonly dossierDecidable = computed(() => this.op()?.kycStatus === 'UNDER_REVIEW');
  readonly dossierApproving = signal(false);
  readonly dossierError = signal<string | null>(null);
  readonly dossierFlash = signal<string | null>(null);
  readonly dossierRejectOpen = signal(false);
  readonly dossierRejecting = signal(false);
  readonly dossierRejectError = signal<string | null>(null);
  readonly dossierRejectForm = this.fb.group({
    reason: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(500)]],
  });

  // ── Approve modal ─────────────────────────────────────────────────────
  readonly approveOpen = signal(false);
  readonly approving = signal(false);
  readonly approveError = signal<string | null>(null);
  readonly approveForm = this.fb.group({
    licenseTier: ['BRONZE' as LicenseTier, [Validators.required]],
    customTotalPct: [null as number | null],
    customPlatformPct: [null as number | null],
    customOperatorPct: [null as number | null],
  });

  // ── Credit reference modal ────────────────────────────────────────────
  readonly creditOpen = signal(false);
  readonly creditSaving = signal(false);
  readonly creditError = signal<string | null>(null);
  readonly creditForm = this.fb.group({
    amount: [null as number | null],
    reason: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(280)]],
  });

  // ── Commission override modal ────────────────────────────────────────
  readonly commissionOpen = signal(false);
  readonly commissionSaving = signal(false);
  readonly commissionError = signal<string | null>(null);
  /** `useGlobal: true` → on PUT all-null (clear l'override).
   *  `useGlobal: false` → on envoie les 3 valeurs custom. */
  readonly commissionForm = this.fb.group({
    useGlobal: [true],
    customTotalPct: [0, [Validators.min(0), Validators.max(100)]],
    customPlatformPct: [0, [Validators.min(0), Validators.max(100)]],
    customOperatorPct: [0, [Validators.min(0), Validators.max(100)]],
  });

  readonly canApprove = computed(() => this.op()?.operatorStatus === 'PENDING');

  /** Link to the wallet detail page for this operator.
   *  Special case: the platform fallback operator (`isFallback && isSystemOwned`)
   *  doesn't have its own OPERATOR wallet — it shares the PLATFORM wallet
   *  (ownerType=PLATFORM, ownerRef=0 by convention). */
  readonly walletLink = computed<unknown[] | null>(() => {
    const op = this.op();
    if (!op) return null;
    if (op.isFallback === true && op.isSystemOwned === true) {
      return ['/wallets', 'PLATFORM', 0];
    }
    return ['/wallets', 'OPERATOR', op.id];
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.service
      .get(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (op) => {
          this.op.set(op);
          this.loading.set(false);
          // Trigger secondary loads — failures here are non-blocking,
          // the page reste utilisable même sans les taux effectifs.
          this.loadEffectiveRates();
          this.loadGlobalConfig();
          this.loadKycDocs();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cet opérateur.'),
          );
        },
      });
  }

  private loadEffectiveRates(): void {
    this.service
      .effectiveRates(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => this.effectiveRates.set(r),
        error: () => this.effectiveRates.set(null),
      });
  }

  private loadGlobalConfig(): void {
    this.commissionService
      .getGlobal()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (c) => this.globalConfig.set(c),
        error: () => this.globalConfig.set(null),
      });
  }

  // ── Justificatifs KYC (#66) ───────────────────────────────────────────

  /** Documents KYC de cet opérateur — tous statuts confondus. */
  loadKycDocs(): void {
    this.kycLoading.set(true);
    this.kycError.set(null);
    this.kyc
      .bySubject('OPERATOR', this.id, 1, 50)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.kycDocs.set(res.data ?? []);
          this.kycLoading.set(false);
        },
        error: (err: unknown) => {
          this.kycDocs.set([]);
          this.kycLoading.set(false);
          this.kycError.set(toErrorMessage(err, 'Impossible de charger les justificatifs.'));
        },
      });
  }

  /** Un PDF n'est pas affichable en lightbox → on retombe sur un lien « Ouvrir ». */
  isImage(url: string | null | undefined): boolean {
    return !!url && IMAGE_EXT.test(url);
  }

  validateDoc(doc: KycDocument): void {
    if (this.kycActingId() !== null) return;
    this.kycActingId.set(doc.id);
    this.kycError.set(null);
    this.kyc
      .validate(doc.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.kycActingId.set(null);
          this.patchDoc(updated);
        },
        error: (err: unknown) => {
          this.kycActingId.set(null);
          this.kycError.set(toErrorMessage(err, 'La validation du document a échoué.'));
        },
      });
  }

  openDocReject(doc: KycDocument): void {
    this.docRejectTarget.set(doc);
    this.docRejectForm.reset({ reason: '' });
    this.docRejectError.set(null);
    this.docRejectOpen.set(true);
  }

  closeDocReject(): void {
    if (this.docRejecting()) return;
    this.docRejectOpen.set(false);
    this.docRejectTarget.set(null);
  }

  submitDocReject(): void {
    const target = this.docRejectTarget();
    if (!target || this.docRejectForm.invalid || this.docRejecting()) {
      this.docRejectForm.markAllAsTouched();
      return;
    }
    this.docRejecting.set(true);
    this.docRejectError.set(null);
    this.kyc
      .reject(target.id, { reason: this.docRejectForm.controls.reason.value.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.docRejecting.set(false);
          this.docRejectOpen.set(false);
          this.docRejectTarget.set(null);
          this.patchDoc(updated);
        },
        error: (err: unknown) => {
          this.docRejecting.set(false);
          this.docRejectError.set(toErrorMessage(err, 'Le rejet du document a échoué.'));
        },
      });
  }

  /**
   * Patche le document en place — il reste listé, seul son statut change.
   *
   * ⚠️ **Fusion, pas remplacement** : `validate`/`reject` renvoient le
   * `KycDocument` brut, alors que le listing (`/kyc/subject`) renvoie un
   * `KycDocumentView` enrichi (`subjectName`/`user`). Remplacer perdrait
   * l'enrichissement.
   */
  private patchDoc(updated: KycDocument): void {
    this.kycDocs.set(
      this.kycDocs().map((d) => (d.id === updated.id ? { ...d, ...updated } : d)),
    );
  }

  // ── Décision sur le dossier (#66) ─────────────────────────────────────

  approveDossier(): void {
    if (this.dossierApproving() || !this.dossierDecidable()) return;
    this.dossierApproving.set(true);
    this.dossierError.set(null);
    this.dossierFlash.set(null);
    this.service
      .approveKyc(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (view) => {
          this.dossierApproving.set(false);
          this.patchDossier(view.kycStatus ?? 'VALIDATED', null);
          this.dossierFlash.set('Dossier validé — cet opérateur peut désormais recruter.');
        },
        error: (err: unknown) => {
          this.dossierApproving.set(false);
          this.dossierError.set(
            toErrorMessage(err, 'La validation du dossier a échoué.', {
              409: "Ce dossier n'est plus en attente de validation.",
            }),
          );
        },
      });
  }

  openDossierReject(): void {
    this.dossierRejectForm.reset({ reason: '' });
    this.dossierRejectError.set(null);
    this.dossierRejectOpen.set(true);
  }

  closeDossierReject(): void {
    if (this.dossierRejecting()) return;
    this.dossierRejectOpen.set(false);
  }

  submitDossierReject(): void {
    if (this.dossierRejectForm.invalid || this.dossierRejecting()) {
      this.dossierRejectForm.markAllAsTouched();
      return;
    }
    const reason = this.dossierRejectForm.controls.reason.value.trim();
    this.dossierRejecting.set(true);
    this.dossierRejectError.set(null);
    this.service
      .rejectKyc(this.id, reason)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (view) => {
          this.dossierRejecting.set(false);
          this.dossierRejectOpen.set(false);
          this.patchDossier(view.kycStatus ?? 'REJECTED', view.kycRejectionReason ?? reason);
          this.dossierFlash.set("Dossier rejeté — l'opérateur a été notifié du motif.");
        },
        error: (err: unknown) => {
          this.dossierRejecting.set(false);
          this.dossierRejectError.set(
            toErrorMessage(err, 'Le rejet du dossier a échoué.', {
              409: "Ce dossier n'est plus en attente de validation.",
            }),
          );
        },
      });
  }

  /**
   * Le back renvoie un `OperatorView` (plus pauvre que le `Operator` de la fiche) :
   * on ne reporte que les champs KYC pour ne pas perdre le reste.
   */
  private patchDossier(status: OperatorKycStatus, reason: string | null): void {
    const current = this.op();
    if (!current) return;
    this.op.set({ ...current, kycStatus: status, kycRejectionReason: reason });
  }

  // ── Approve ───────────────────────────────────────────────────────────
  openApprove(): void {
    const op = this.op();
    this.approveForm.reset({
      licenseTier: op?.licenseTier ?? 'BRONZE',
      customTotalPct: op?.customTotalPct ?? null,
      customPlatformPct: op?.customPlatformPct ?? null,
      customOperatorPct: op?.customOperatorPct ?? null,
    });
    this.approveError.set(null);
    this.approveOpen.set(true);
  }
  closeApprove(): void {
    if (this.approving()) return;
    this.approveOpen.set(false);
  }
  submitApprove(): void {
    if (this.approveForm.invalid || this.approving()) {
      this.approveForm.markAllAsTouched();
      return;
    }
    const raw = this.approveForm.getRawValue();
    this.approving.set(true);
    this.approveError.set(null);
    this.service
      .approve(this.id, {
        licenseTier: raw.licenseTier,
        customTotalPct: raw.customTotalPct,
        customPlatformPct: raw.customPlatformPct,
        customOperatorPct: raw.customOperatorPct,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.approving.set(false);
          this.approveOpen.set(false);
          this.fetch();
        },
        error: (err: unknown) => {
          this.approving.set(false);
          this.approveError.set(toErrorMessage(err, "L'approbation a échoué."));
        },
      });
  }

  // ── Credit reference ──────────────────────────────────────────────────
  openCredit(): void {
    const op = this.op();
    this.creditForm.reset({
      amount: op?.creditReferenceAmount ?? null,
      reason: '',
    });
    this.creditError.set(null);
    this.creditOpen.set(true);
  }
  closeCredit(): void {
    if (this.creditSaving()) return;
    this.creditOpen.set(false);
  }
  submitCredit(): void {
    if (this.creditForm.invalid || this.creditSaving()) {
      this.creditForm.markAllAsTouched();
      return;
    }
    const raw = this.creditForm.getRawValue();
    this.creditSaving.set(true);
    this.creditError.set(null);
    this.service
      .setCreditReference(this.id, {
        amount: raw.amount,
        reason: raw.reason.trim(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.creditSaving.set(false);
          this.creditOpen.set(false);
          this.fetch();
        },
        error: (err: unknown) => {
          this.creditSaving.set(false);
          this.creditError.set(
            toErrorMessage(err, 'La mise à jour de la référence a échoué.'),
          );
        },
      });
  }

  // ── Commission override ──────────────────────────────────────────────
  openCommission(): void {
    const op = this.op();
    const global = this.globalConfig();
    const hasOverride = op?.customTotalPct != null;
    this.commissionForm.reset({
      useGlobal: !hasOverride,
      // Pre-fill with current values — override si présent, sinon défaut global.
      customTotalPct: op?.customTotalPct ?? global?.defaultTotalPct ?? 0,
      customPlatformPct: op?.customPlatformPct ?? global?.defaultPlatformPct ?? 0,
      customOperatorPct: op?.customOperatorPct ?? global?.defaultOperatorPct ?? 0,
    });
    this.commissionError.set(null);
    this.commissionOpen.set(true);
  }
  closeCommission(): void {
    if (this.commissionSaving()) return;
    this.commissionOpen.set(false);
  }
  submitCommission(): void {
    if (this.commissionSaving()) return;
    const raw = this.commissionForm.getRawValue();
    this.commissionError.set(null);

    let body: { customTotalPct: number | null; customPlatformPct: number | null; customOperatorPct: number | null };
    if (raw.useGlobal) {
      // Clear l'override : tous null.
      body = { customTotalPct: null, customPlatformPct: null, customOperatorPct: null };
    } else {
      // Validation : total = plateforme + opérateur (±0.001 marge flottante).
      const sum = Number(raw.customPlatformPct) + Number(raw.customOperatorPct);
      if (Math.abs(sum - Number(raw.customTotalPct)) > 0.001) {
        this.commissionError.set(
          `Total (${raw.customTotalPct}%) doit être égal à plateforme (${raw.customPlatformPct}%) + opérateur (${raw.customOperatorPct}%) = ${sum}%.`,
        );
        return;
      }
      body = {
        customTotalPct: Number(raw.customTotalPct),
        customPlatformPct: Number(raw.customPlatformPct),
        customOperatorPct: Number(raw.customOperatorPct),
      };
    }

    this.commissionSaving.set(true);
    this.commissionService
      .setOperatorOverride(this.id, body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.commissionSaving.set(false);
          this.commissionOpen.set(false);
          this.fetch();
        },
        error: (err: unknown) => {
          this.commissionSaving.set(false);
          this.commissionError.set(
            toErrorMessage(err, 'La mise à jour des taux a échoué.', {
              400: 'Validation backend : vérifie que total = plateforme + opérateur.',
            }),
          );
        },
      });
  }
}
